<!-- Loading State -->
<ng-container *ngIf="isLoading()">
  <div class="mb-4">
    <div class="skeleton-loader skeleton-title"></div>
  </div>
  <div class="row g-3 mb-4">
    <div class="col-md-3" *ngFor="let i of [1, 2, 3, 4]">
      <div class="card bg-card">
        <div class="card-body p-3">
          <div class="skeleton-loader skeleton-label mb-2"></div>
          <div class="skeleton-loader skeleton-value mb-2"></div>
          <div class="skeleton-loader skeleton-text-sm w-50"></div>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Error State -->
<ng-container *ngIf="!isLoading() && error()">
  <div class="text-center py-5">
    <i class="bi bi-exclamation-triangle fs-1 d-block mb-3 text-state-error"></i>
    <h5>Something went wrong</h5>
    <p class="text-muted">{{ error() }}</p>
  </div>
</ng-container>

<!-- Empty State (No Active Season) -->
<ng-container *ngIf="!isLoading() && !error() && !hasActiveSeason">
  <div class="text-center py-5">
    <i class="bi bi-calendar-x fs-1 d-block mb-3 text-muted"></i>
    <h5>No Active Season</h5>
    <p class="text-muted">
      No active season configured. Contact your administrator to set up your spray season.
    </p>
  </div>
</ng-container>

<!-- Dashboard Content -->
<ng-container *ngIf="!isLoading() && !error() && hasActiveSeason && dashboard() as data">

  <!-- Preview Banner -->
  <div class="preview-banner mb-4 p-3 rounded d-flex align-items-start gap-3">
    <i class="bi bi-rocket-takeoff-fill fs-4 flex-shrink-0 text-primary"></i>
    <div>
      <h6 class="fw-bold mb-1">Acre Advantage is Coming Soon!</h6>
      <p class="mb-0 small text-muted">
        We're building something exciting. The points and sponsor data shown below are sample data to give you a preview of how Acre Advantage will work. Stay tuned!
      </p>
    </div>
  </div>

  <!-- Stat Cards Row -->
  <div class="row g-3 mb-4">
    <!-- Total Points Earned -->
    <div class="col-md-3">
      <div class="card stat-card stat-card--blue">
        <div class="card-body p-3 d-flex align-items-center gap-3">
          <div class="stat-icon stat-icon--blue">
            <i class="bi bi-star-fill"></i>
          </div>
          <div>
            <h3 class="stat-value mb-0 fw-bold">
              {{ formatNumber(data.totalPointsEarned) }}
            </h3>
            <p class="mb-0 small fw-medium text-muted">Total Points Earned</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Eligible Records Used -->
    <div class="col-md-3">
      <div class="card stat-card stat-card--green">
        <div class="card-body p-3 d-flex align-items-center gap-3">
          <div class="stat-icon stat-icon--green">
            <i class="bi bi-check-circle-fill"></i>
          </div>
          <div>
            <h3 class="stat-value mb-0 fw-bold">
              {{ formatNumber(data.eligibleRecordsUsed) }}
            </h3>
            <p class="mb-0 small fw-medium text-muted">Eligible Records Used</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Total Spray Records -->
    <div class="col-md-3">
      <div class="card stat-card stat-card--green">
        <div class="card-body p-3 d-flex align-items-center gap-3">
          <div class="stat-icon stat-icon--green">
            <i class="bi bi-clock-fill"></i>
          </div>
          <div>
            <h3 class="stat-value mb-0 fw-bold">
              {{ formatNumber(data.totalSprayRecords) }}
            </h3>
            <p class="mb-0 small fw-medium text-muted">Total Spray Records</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Points Missed -->
    <div class="col-md-3">
      <div class="card stat-card stat-card--amber">
        <div class="card-body p-3 d-flex align-items-center gap-3">
          <div class="stat-icon stat-icon--amber">
            <i class="bi bi-lightbulb-fill"></i>
          </div>
          <div>
            <h3 class="stat-value mb-0 fw-bold">
              {{ formatNumber(data.totalPointsMissed) }}
            </h3>
            <p class="mb-0 small fw-medium text-muted">Points Missed</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Two-Column Layout: Points by Sponsor + Additional Earning Potential -->
  <div class="row g-3">

    <!-- Points by Sponsor (left ~2/3) -->
    <div class="col-lg-8">
      <div class="d-flex align-items-center gap-2 mb-3">
        <i class="bi bi-grid-3x3-gap-fill text-muted"></i>
        <h5 class="mb-0 fw-bold">Points by Sponsor</h5>
      </div>

      <!-- Sponsor cards in a flex-wrap row -->
      <div class="d-flex flex-wrap gap-3">
        <div
          *ngFor="let sponsor of data.sponsorBreakdowns"
          class="card sponsor-card flex-fill"
          (click)="openSponsorDetail(sponsor.sponsorId)"
        >
          <div class="card-body p-3">
            <!-- Sponsor logo or name -->
            <div
              class="sponsor-logo-container mb-3"
              [class.has-brand-color]="sponsor.brandColor && sponsor.sponsorLogoUrl"
              [style.background-color]="(sponsor.brandColor && sponsor.sponsorLogoUrl) ? sponsor.brandColor : null"
            >
              <img
                *ngIf="sponsor.sponsorLogoUrl"
                [src]="getLogoFullUrl(sponsor.sponsorLogoUrl)"
                [alt]="sponsor.sponsorName + ' logo'"
                class="sponsor-logo"
              />
              <span
                *ngIf="!sponsor.sponsorLogoUrl"
                class="sponsor-name-fallback fw-bold"
              >
                {{ sponsor.sponsorName }}
              </span>
            </div>

            <!-- Total Points -->
            <div class="mb-3">
              <span class="text-uppercase small fw-bold d-block text-muted ls-wide">
                Total Points
              </span>
              <span
                class="sponsor-total-points fw-bold"
              >
                {{ formatNumber(sponsor.totalPoints) }}
              </span>
              <span class="small fw-medium text-muted"> pts</span>
            </div>

            <!-- Chemical Line Items -->
            <div class="chemical-list">
              <div
                *ngFor="let chemical of sponsor.chemicals; let last = last"
                class="chemical-item d-flex justify-content-between align-items-center py-2"
                [class.border-bottom]="!last"
              >
                <div class="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                  <span class="chemical-name text-truncate">
                    {{ chemical.chemicalName }}
                  </span>
                  <span
                    class="category-badge"
                    [style.background-color]="getCategoryBadgeColor(chemical.category)"
                    [style.color]="getCategoryTextColor(chemical.category)"
                  >
                    {{ chemical.category }}
                  </span>
                </div>
                <div class="text-end ms-2 flex-shrink-0">
                  <span class="fw-bold">
                    {{ formatNumber(chemical.points) }}
                  </span>
                  <span class="small text-muted"> pts</span>
                </div>
              </div>
            </div>

            <!-- Product Details hint -->
            <div class="text-center mt-3">
              <span class="small fw-medium text-muted">
                <i class="bi bi-box-arrow-up-right me-1"></i>Click for product details
              </span>
            </div>
          </div>
        </div>

        <!-- Empty state when no sponsors -->
        <div *ngIf="data.sponsorBreakdowns.length === 0" class="text-center py-4 w-100">
          <i class="bi bi-award fs-2 d-block mb-2 text-muted opacity-50"></i>
          <p class="text-muted">No sponsor points earned yet this season.</p>
        </div>
      </div>
    </div>

    <!-- Additional Earning Potential (right ~1/3) -->
    <div class="col-lg-4">
      <div class="d-flex align-items-center gap-2 mb-3">
        <i class="bi bi-lightbulb-fill text-warning"></i>
        <h5 class="mb-0 fw-bold">Additional Earning Potential</h5>
      </div>

      <div class="card bg-card">
        <div class="card-body p-3">
          <!-- Total Available -->
          <div class="mb-3">
            <span class="text-uppercase small fw-bold d-block text-muted ls-wide">
              Additional Points Available
            </span>
            <span class="earning-potential-total fw-bold text-points-missed">
              {{ formatNumber(data.totalPointsMissed) }}
            </span>
          </div>

          <!-- Earning Potential Breakdowns List -->
          <div *ngIf="data.missedBreakdowns.length > 0" class="earning-potential-list">
            <div
              *ngFor="let missed of data.missedBreakdowns; let last = last"
              class="earning-potential-item py-2 d-flex justify-content-between align-items-start"
              [class.border-bottom]="!last"
            >
              <div class="min-w-0">
                <span class="fw-medium d-block">
                  Switch to {{ missed.chemicalName }}
                </span>
                <div class="d-flex align-items-center gap-2 mt-1">
                  <span class="small text-muted">
                    {{ missed.recordCount }} records
                  </span>
                  <span
                    class="category-badge"
                    [style.background-color]="getCategoryBadgeColor(missed.category)"
                    [style.color]="getCategoryTextColor(missed.category)"
                  >
                    {{ missed.category }}
                  </span>
                </div>
              </div>
              <span class="fw-bold flex-shrink-0 ms-2 text-points-missed">
                +{{ formatNumber(missed.points) }} pts
              </span>
            </div>
          </div>

          <!-- Placeholder state: tracking not yet available (pre-implementation) -->
          <div *ngIf="data.missedBreakdowns.length === 0 && data.totalPointsMissed === 0" class="py-3 text-center">
            <i class="bi bi-lightbulb d-block mb-2 text-muted fs-1-5rem opacity-50"></i>
            <p class="mb-0 small fw-medium text-muted">
              Coming soon -- we're setting up earning potential tracking for your organization.
            </p>
          </div>

          <!-- Motivational callout box (only shown when totalPointsMissed > 0) -->
          <div
            *ngIf="data.totalPointsMissed > 0"
            class="earning-potential-callout mt-3 p-3 rounded"
          >
            <p class="mb-0 small">
              You have <strong>{{ formatNumber(data.totalPointsMissed) }} additional pts</strong>
              available by using Acre Advantage partner products on eligible spray records.
            </p>
            <p class="mb-0 small mt-1 earning-potential-note">
              Subject to product availability and label requirements in your area.
            </p>
          </div>
        </div>
      </div>
    </div>

  </div>
</ng-container>

<!-- How to Buy Modal -->
<app-how-to-buy-modal
  [sponsorId]="selectedSponsorId || ''"
  [visible]="showHowToBuyModal"
  [dummyData]="dummyContactData"
  (close)="closeSponsorDetail()">
</app-how-to-buy-modal>
